import { CalendarRange, RotateCcw, SlidersHorizontal } from 'lucide-react';

import { GRANULARITIES, PRESETS, STATUSES } from '@/features/reports/lib/filters';
import { formatJalaliDate } from '@/lib/format';
import JalaliDatePicker from '@/components/JalaliDatePicker';

/**
 * The single filter surface shared by every report page.
 *
 * State lives in the URL (see `useReportFilters`), so this component is fully
 * controlled: it renders what the URL says and reports changes upward.
 */
export default function ReportFilters({
    filters,
    update,
    applyPreset,
    reset,
    activePreset,
    granularityRelevant = true,
}) {
    const rangeInvalid = Boolean(filters.from && filters.to && filters.from > filters.to);

    return (
        <div className="card report-filters">
            <div className="report-filters-row">
                <div className="report-filter-group">
                    <span className="report-filter-label">
                        <CalendarRange size={15} />
                        بازه زمانی
                    </span>
                    <div className="report-chips">
                        {PRESETS.map((preset) => (
                            <button
                                key={preset.key}
                                type="button"
                                onClick={() => applyPreset(preset.key)}
                                className={`report-chip ${activePreset === preset.key ? 'active' : ''}`}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="report-filter-group">
                    <div className="report-date-inputs">
                        <label className="report-date-field">
                            <span>از</span>
                            <JalaliDatePicker
                                value={filters.from ?? ''}
                                onChange={(from) => update({ from: from || null })}
                                className="report-date-input"
                                aria-label="از تاریخ شمسی"
                                rangeStart={filters.from}
                                rangeEnd={filters.to}
                            />
                            <small>{filters.from ? formatJalaliDate(filters.from) : 'پیش‌فرض سرور'}</small>
                        </label>

                        <label className="report-date-field">
                            <span>تا</span>
                            <JalaliDatePicker
                                value={filters.to ?? ''}
                                onChange={(to) => update({ to: to || null })}
                                className="report-date-input"
                                aria-label="تا تاریخ شمسی"
                                rangeStart={filters.from}
                                rangeEnd={filters.to}
                            />
                            <small>{filters.to ? formatJalaliDate(filters.to) : 'امروز'}</small>
                        </label>
                    </div>
                </div>
            </div>

            <div className="report-filters-row report-filters-row-secondary">
                {granularityRelevant && (
                    <div className="report-filter-group">
                        <span className="report-filter-label">
                            <SlidersHorizontal size={15} />
                            تفکیک
                        </span>
                        <div className="report-segmented">
                            {GRANULARITIES.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => update({ granularity: option.value })}
                                    className={`report-segment ${filters.granularity === option.value ? 'active' : ''}`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="report-filter-group">
                    <span className="report-filter-label">وضعیت سفارش</span>
                    <select
                        value={filters.status}
                        onChange={(event) => update({ status: event.target.value })}
                        className="report-select"
                    >
                        {STATUSES.map((option) => (
                            <option key={option.value || 'all'} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <button type="button" onClick={reset} className="report-reset-btn">
                    <RotateCcw size={15} />
                    بازنشانی
                </button>
            </div>

            {rangeInvalid && (
                <p className="report-filter-warning">
                    تاریخ شروع بعد از تاریخ پایان است؛ بازه‌ای معتبر انتخاب کنید.
                </p>
            )}

            <p className="report-filter-note">
                در حالت «همه وضعیت‌ها» سفارش‌های ناموفق از مبالغ حذف می‌شوند. برای دیدن آن‌ها وضعیت
                «ناموفق» را انتخاب کنید.
            </p>
        </div>
    );
}
